import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../state/ApiContext';
import type { CardRef } from '../client';
import type { WikiRefProject } from '../lib/wiki-refs';

/**
 * The team-scoped data every reference-rendering surface needs: card
 * refs for #KEY-N and the REAL project list for wiki:KEY/path and
 * [[KEY:path]] — so wiki links resolve even in teams/projects with no
 * cards. Fetch failures degrade to empty lists (refs render as plain
 * text, never an error).
 */
export interface TeamRefData {
  cardRefs: CardRef[];
  projects: WikiRefProject[];
  /** Re-fetch card refs (e.g. when a message cites an unknown #KEY-N). */
  refreshCardRefs: () => void;
}

export function useTeamRefContext(
  teamId: string | number | undefined,
): TeamRefData {
  const { projectsApi } = useApi();
  const [cardRefs, setCardRefs] = useState<CardRef[]>([]);
  const [projects, setProjects] = useState<WikiRefProject[]>([]);

  const teamIdNum = teamId === undefined ? undefined : Number(teamId);

  const refreshCardRefs = useCallback(() => {
    if (teamIdNum === undefined || Number.isNaN(teamIdNum)) return;
    projectsApi
      ?.apiTeamsTeamIdCardRefsGet?.(teamIdNum)
      .then((response) => setCardRefs(response.data.cardRefs || []))
      .catch(() => setCardRefs([]));
  }, [projectsApi, teamIdNum]);

  useEffect(() => {
    refreshCardRefs();
  }, [refreshCardRefs]);

  useEffect(() => {
    if (teamIdNum === undefined || Number.isNaN(teamIdNum)) return;
    projectsApi
      ?.apiTeamsTeamIdProjectsGet?.(teamIdNum)
      .then((response) => {
        const raw = response.data.projects || [];
        setProjects(
          raw
            .filter((project: { id?: number; key?: string }) =>
              Boolean(project.id && project.key),
            )
            .map((project: { id?: number; key?: string }) => ({
              key: project.key!.toUpperCase(),
              projectId: project.id!,
            })),
        );
      })
      .catch(() => setProjects([]));
  }, [projectsApi, teamIdNum]);

  return { cardRefs, projects, refreshCardRefs };
}
